import { BlockPlaybook, Playbook } from '@driftt/api-types'
import { Button, P } from '@driftt/tide-core'
import { Modal, ModalBody, ModalFooter, ModalTitle } from '@driftt/tide-modal'
import { Subnav as SideNav } from '@driftt/tide-subnav'
import { showTransientNotification } from '@driftt/traffic-control'
import produce from 'immer'
import { equals, isEmpty, isNil, path, pathOr } from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import { ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  NavLink,
  Prompt,
  Redirect,
  RouteComponentProps,
} from 'react-router-dom'

import { fetchPlaybookFormattedBlock } from 'api/blocks'
import { getClassifications } from 'api/playbooks'
import BlockEditorNavigation from 'components/BlockEditorNavigation'
import Editor from 'components/EditorContext' // lazy import?
import TransientBanner from 'components/TransientBanner'
import UniversalLoader from 'components/UniversalLoader'
import { useConnectedCalendarIds } from 'hooks/useConnectedCalendarIds'
import { useConsumingPlaybooks } from 'hooks/useConsumingPlaybooks'
import { useTeams } from 'hooks/useTeams'
import { receiveClassifications } from 'modules/classifications/actions'
import { receivePlaybook } from 'modules/playbook/actions'
import { validatePlaybook } from 'modules/playbookValidation/actions'
import { userHasAnyPlaybookAccess } from 'utils/access'
import { getSubNavNodes } from 'utils/getSubNavNodes'

interface TransientStateProps {
  showTransientBanner: boolean
  transientStatePayload: {
    body: ReactElement
    action: {
      handler: () => void
      title: string
    }
  }
}

interface EditorRoutingProps extends RouteComponentProps {
  match: {
    params: {
      id: string
    }
    isExact: boolean
    path: string
    url: string
  }
}

export interface BlockEditorOnChangeProps {
  playbook: BlockPlaybook
}

const BlockEditorLayout = ({ location, match }: EditorRoutingProps) => {
  const dispatch = useDispatch()

  const currentBlock: Playbook | undefined = useSelector((state) =>
    pathOr(undefined, ['__PLAYBOOK_EDITOR', 'playbook'], state)
  )

  const blocksById = useSelector(
    path(['__BOT_EDITOR_v2', 'blocks', 'blocksById'])
  )

  const orgId = useSelector(path(['org', 'id']))

  const { id: blockId } = match.params

  const connectedCalendarIds = useConnectedCalendarIds()
  const orgTeams = useTeams()

  const keepGoalConvoStatus = new URLSearchParams(window.location.search).get(
    'keepGoalConvoStatus'
  )

  const [isLoading, setIsLoading] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showTransientBanner, setShowTransientBanner] = useState(false)
  const [transientStatePayload, setTransientStatePayload] = useState({})
  const [clonedWithGoalsModalOpen, setClonedWithGoalsModalOpen] = useState(
    !isNil(keepGoalConvoStatus)
  )
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false)

  const SUB_NAV_NODES = getSubNavNodes()

  const onCloseModal = () => {
    setClonedWithGoalsModalOpen(false)
  }
  const handleTransientState = ({
    showTransientBanner: showBanner,
    transientStatePayload: payload,
  }: TransientStateProps) => {
    setShowTransientBanner(showBanner)
    setTransientStatePayload(payload)
  }

  const [consumingPlaybookData, { isLoading: consumingPlaybooksLoading }] =
    useConsumingPlaybooks(blockId)

  const receiveBlockData = useCallback(
    async ([{ data: blockData }, { data: classificationData }]) => {
      dispatch(receivePlaybook(blockData))
      dispatch(receiveClassifications(classificationData))

      setIsLoading(false)
    },
    [dispatch, setIsLoading]
  )

  useEffect(() => {
    async function fetchBlock() {
      setIsLoading(true)
      try {
        await Promise.all([
          fetchPlaybookFormattedBlock(blockId),
          getClassifications(),
        ]).then(receiveBlockData)
      } catch (e) {
        console.warn(e)
        showTransientNotification({
          type: 'error',
          message: 'We could not locate your block.',
        })
        setHasFailed(true)
      }
    }

    const currentBlockIdFromStore = path(['meta', 'blockId'], currentBlock)

    if (isEmpty(currentBlock) || currentBlockIdFromStore !== blockId) {
      fetchBlock()
    }
  }, [receiveBlockData, currentBlock, blockId])

  useEffect(() => {
    if (hasChanges) {
      window.onbeforeunload = () => true
    } else {
      window.onbeforeunload = null
    }
  }, [hasChanges])

  const onBlockEditorChange = async ({
    playbook: block,
  }: BlockEditorOnChangeProps) => {
    const updatedBlock = produce((reusableBlock: Playbook | undefined) => {
      if (!!reusableBlock) {
        reusableBlock.actions[0].action = block.actions[0].action
        reusableBlock.actionGroups = block.actionGroups
      }
    })

    dispatch(
      validatePlaybook(block, blocksById, connectedCalendarIds, orgTeams)
    )

    try {
      if (!equals(currentBlock, block)) {
        setHasChanges(true)
        dispatch(receivePlaybook(updatedBlock(currentBlock)))
      }
    } catch (error) {
      setShowTransientBanner(true)

      console.warn(error)
    }
  }

  const refreshLocalBlockState = useCallback(
    (updatedBlock: BlockPlaybook) => {
      setHasChanges(false)
      dispatch(receivePlaybook(updatedBlock))
    },
    [setHasChanges, dispatch]
  )

  if (hasFailed) {
    return <Redirect to="/not-found" />
  }

  if (isLoading || !orgId) {
    return (
      <UniversalLoader
        /* tslint:disable-next-line */
        className="playbook-editor-loading"
        size="large"
        loaderText="Gathering your block data..."
      />
    )
  }

  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()

  return (
    <>
      {showTransientBanner && (
        <TransientBanner payload={transientStatePayload} />
      )}
      {hasAnyPlaybookAccess && (
        <SideNav
          open={sidebarIsOpen}
          title="Playbooks"
          nodes={SUB_NAV_NODES}
          NavLink={NavLink}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
      )}
      <div className="playbook-editor-layout block-editor-layout">
        {/* @ts-ignore  BV2 TODO: remove */}
        {!consumingPlaybooksLoading && currentBlock && (
          <BlockEditorNavigation
            publishedBlock={currentBlock}
            hasChanges={hasChanges}
            stateRefresh={refreshLocalBlockState}
            consumingPlaybookData={consumingPlaybookData}
            blockId={blockId}
            onBlockEditorChange={onBlockEditorChange}
          />
        )}
        <section className="playbook-editor-content-area">
          <Editor
            key={location.pathname}
            isPreviewState={false}
            type="BOT"
            title="Start of reusable block"
            onChange={onBlockEditorChange}
            onTransientChange={handleTransientState}
            playbook={currentBlock}
            isAIEnabled={false}
          />
        </section>
      </div>
      <Modal
        size="small"
        open={clonedWithGoalsModalOpen}
        onClose={onCloseModal}
      >
        <ModalTitle>
          {keepGoalConvoStatus === 'true'
            ? 'Conversation status set according to goal settings from playbook'
            : 'Goals converted to open paths'}
        </ModalTitle>
        <ModalBody>
          <P style={{ marginTop: 0, marginBottom: 0 }}>
            {keepGoalConvoStatus === 'true'
              ? `When cloning your playbook, you chose to set the end of conversation status according to goal settings. If you'd like to continue using goals outside of this block, be sure to set the conversation status to “Keep open.”`
              : `When cloning your playbook, you chose to convert all goals to open paths. If you'd like to continue using goals, you can add them in the playbooks where this block is used as long as the conversation status remains as “Keep open.”`}
          </P>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onCloseModal}>Got it!</Button>
        </ModalFooter>
      </Modal>
      <Prompt
        when={hasChanges}
        message="You have unsaved changes, are you sure you want to leave?"
      />
    </>
  )
}

export default BlockEditorLayout
